import { DsIconButton, useScreenSize } from '@frontends/ds';
import { IbProgramSelect } from '@frontends/feature-ib';
import { TradingSessionAlertTSX } from '@frontends/feature-trading-session-alert';
import { useLocale } from '@frontends/util-i18n';
import { useTheme } from '@frontends/util-theme-provider';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';
import useIbDeskNavigation from '@/hooks/useIbDeskNavigation';
import useIbDeskSection from '@/hooks/useIbDeskSection';
import { LanguageSelector } from './LanguageSelector';
import { ShareFeedbackLink } from './ShareFeedbackLink';
import { ThemeToggle } from './ThemeToggle';

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/sessions': 'sessions',
  '/accounts': 'accounts',
  '/services/certificates': 'certificates_title',
  '/services/trading-ideas': 'trading_ideas_title',
};

const ProtectedSideHeader = memo(function ProtectedSideHeader({
  menuButtonId,
  onMenuOpen,
}: {
  menuButtonId: string;
  onMenuOpen: () => void;
}) {
  const { isDesktop } = useScreenSize();
  const localeContext = useLocale();
  const { resolvedMode } = useTheme();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isIbDeskSection = useIbDeskSection();
  const ibNavigation = useIbDeskNavigation();
  const routePath = Object.keys(PAGE_TITLE_KEYS).find((path) => matchPath(path, pathname) !== null);
  const routeTitle = routePath ? t(PAGE_TITLE_KEYS[routePath]) : null;
  const pageTitle = isIbDeskSection ? ibNavigation.title : routeTitle;
  const hasTitleRow = !isDesktop || Boolean(pageTitle);

  return (
    <header className="flex min-h-16 shrink-0 items-center gap-2 px-4 md:px-6">
      <div className="flex min-h-(--header-height) w-full flex-wrap items-center justify-between py-2 gap-y-2 gap-x-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
          {hasTitleRow && (
            <div className="flex min-w-0 items-center gap-4">
              {!isDesktop && (
                <DsIconButton id={menuButtonId} name="panel-left" onPress={onMenuOpen} aria-label={t('open_menu')} />
              )}

              {pageTitle && <h1 className="truncate text-2xl font-semibold">{pageTitle}</h1>}
            </div>
          )}

          <TradingSessionAlertTSX
            showOpenAlertBeforeMinutes={5}
            showCloseAlertBeforeMinutes={5}
            locale={localeContext.locale.language}
            theme={resolvedMode}
          />
        </div>

        {/* Below md the IB controls drop under the title row; from md ms-auto keeps them next to theme and language */}
        {isIbDeskSection && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 max-md:order-last max-md:basis-full md:ms-auto">
            <IbProgramSelect />
            <ShareFeedbackLink />
          </div>
        )}

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <LanguageSelector />
        </div>
      </div>
    </header>
  );
});

export default ProtectedSideHeader;
